import { i18n } from '@/main'
import { MemberStatus } from '@/models/Groups'
import { TimeFrame } from '@/models/Leaderboard'
import { IUser } from '@/models/Users'
import { GlobalProfileService } from '@/service/GlobalProfileService'
import { GokollabService } from '@/service/GokollabService'
import restAgent from '@/service/restAgent'
import { SocketService } from '@/service/SocketsService'
import { UsersService } from '@/service/UsersService'
import { useGlobalStore } from '@/store/app'
import { isGokollab } from '@ghl/clientportal-core/init'
import { reactive } from 'vue'
import { app } from './app'
import { ConversationState } from './conversations'
import { GroupState } from './groups'
import { Theme } from './themes'

export class UsersState {
  state = reactive({
    processing: false,
    isCurrentUserFetching: false,
    currentUser: undefined as IUser | undefined,
    isCurrentUserGroupInfoFetching: false,
    currenUserGroupInfo: undefined as IUser | undefined,
    usersData: new Map<string, IUser>(),
    showMandatoryInfoModal: false,
    groupContactId: undefined as string | undefined,
  })

  // Helper method to get user rank data from appropriate service
  private async getUserRankData(
    groupId: string,
    userId: string,
    timeframe?: TimeFrame
  ): Promise<any> {
    return isGokollab()
      ? await GokollabService.getUserRankOnLeaderboard(
          groupId,
          userId,
          timeframe
        )
      : await restAgent.ClientPortal.getUserRankOnLeaderboard(
          groupId,
          userId,
          timeframe
        )
  }

  // Track in-flight requests to prevent duplicate API calls
  private inFlightRequests = new Map<string, Promise<IUser>>()

  reset(): void {
    this.state.processing = false
    this.state.usersData = new Map<string, IUser>()
    if (this.state.currentUser)
      this.state.currentUser.gamificationMeta = undefined

    // Clear in-flight requests when resetting
    this.inFlightRequests.clear()
  }

  async getCurrentUser(): Promise<IUser | undefined> {
    try {
      if (this.state.currentUser) {
        return this.state.currentUser
      }
      const locationId = useGlobalStore().$state.locationId
      const id = useGlobalStore().$state.userId
      const response = await restAgent.ClientPortal.getUserDetails(
        locationId,
        id,
        true
      )
      const contactLocationId = response?.data?.contactLocationId
      let gokollabContactId
      if (contactLocationId && isGokollab()) {
        gokollabContactId = await this.getGokollabContactId(
          contactLocationId,
          id
        )
      }
      this.state.currentUser = { ...response.data, gokollabContactId }
      i18n.global.locale.value =
        response?.data?.languagePreference ||
        useGlobalStore().$state.languagePreferenceBuilder ||
        'en'
      Theme.state.isDarkMode = this.state.currentUser?.darkMode ?? false

      // if the user is fetched successfully, it means the user is authenticated we can now initiate socket connection
      SocketService.initializeSocketService(
        ConversationState.handleSocketMessage,
        ConversationState.handleSocketConnectionState
      )

      // useGlobalStore().setProfileDetails(this.state.currentUser)
      return this.state.currentUser
    } catch (error) {
      console.error(error)
    }
  }
  async fetchCurrentUserRankData(timeframe?: TimeFrame): Promise<void> {
    try {
      if (this.state.currentUser?.gamificationMeta?.leaderboard) {
        if (
          timeframe &&
          this.state.currentUser.gamificationMeta.leaderboard[timeframe]
        ) {
          return
        } else if (
          !timeframe &&
          this.state.currentUser.gamificationMeta.leaderboard.size > 0
        ) {
          return
        } else {
          const userRanks = await this.getUserRankData(
            useGlobalStore().$state.groupId,
            useGlobalStore().$state.userId,
            timeframe
          )
          userRanks.data.forEach(userRank => {
            // Access the data property
            this.state.currentUser!.gamificationMeta!.leaderboard!.set(
              userRank.timeFrame,
              userRank.rank
            )
          })
          return
        }
      }
      const userRanks = await this.getUserRankData(
        useGlobalStore().$state.groupId,
        useGlobalStore().$state.userId,
        timeframe
      )
      const gamificationMeta = {
        leaderboard: new Map<TimeFrame, number>(),
      }

      this.state.currentUser!.gamificationMeta = gamificationMeta
      userRanks.data.forEach(userRank => {
        // Access the data property
        this.state.currentUser!.gamificationMeta!.leaderboard!.set(
          userRank.timeFrame,
          userRank.rank
        )
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getUserByContactId(contactId: string): Promise<IUser | undefined> {
    try {
      if (!contactId) {
        return undefined
      }

      // Return cached data if available
      if (this.state.usersData.has(contactId)) {
        return this.state.usersData.get(contactId)
      }

      // Check if there's already an in-flight request for this contactId
      const existingRequest = this.inFlightRequests.get(contactId)
      if (existingRequest) {
        console.log(`Reusing existing API request for contactId: ${contactId}`)
        return existingRequest
      }
      // Create the API request promise
      const requestPromise = this.fetchUserData(contactId)

      // Store the in-flight request
      this.inFlightRequests.set(contactId, requestPromise)

      // Clean up the in-flight request when it completes (success or failure)
      requestPromise.finally(() => {
        this.inFlightRequests.delete(contactId)
      })

      return requestPromise
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  private async fetchUserData(contactId: string): Promise<IUser> {
    try {
      const user = await UsersService.getUserDataBycontactId(contactId)

      if (!user.gamificationMeta) {
        user.gamificationMeta = {
          points: 0,
          level: 1,
        }
      }
      this.state.usersData.set(contactId, user)
      this.state.groupContactId = user.contactId
      return user
    } catch (error) {
      // Set groupContactId separately
      await this.setGroupContactId(contactId)
      console.error('Error fetching user data:', error)
      throw error
    }
  }

  private async setGroupContactId(contactId: string): Promise<void> {
    try {
      if (isGokollab()) {
        // For Gokollab, get white-label contact ID
        const groupId = useGlobalStore().$state.groupId
        const locationId = useGlobalStore().$state.locationId

        if (groupId && locationId) {
          const whiteLabelContactId =
            await GokollabService.getWhiteLabelGroupContactId(
              locationId,
              groupId
            )
          this.state.groupContactId = whiteLabelContactId
        }
      }
    } catch (error) {
      console.error('Error setting group contact ID:', error)
    }
  }

  async getGokollabContactId(
    locationId: string,
    contactId: string
  ): Promise<string | null> {
    try {
      const response = await GokollabService.getGokollabContactId(
        locationId,
        contactId
      )
      return response.contactId
    } catch (error) {
      console.error(error)
      return null
    }
  }
  async handleRemoveOrDeclineRequest(
    contactId: string | undefined,
    _status: MemberStatus,
    declining = false
  ): Promise<void> {
    if (!contactId) {
      throw new Error('Contact ID is required')
    }
    await UsersService.removeUser(contactId)
    GroupState.state.groupData.membersCount -= 1
    if (GroupState.state.requestCount > 0 && declining)
      GroupState.state.requestCount -= 1
  }
  async handleApproveRequest(contactId: string | undefined): Promise<void> {
    if (!contactId) {
      throw new Error('Contact ID is required')
    }
    await UsersService.updateUserGroup({
      contactId: contactId,
      status: MemberStatus.ACTIVE,
    })
    GroupState.state.groupData.membersCount += 1
    if (GroupState.state.requestCount > 0) GroupState.state.requestCount -= 1
  }
  async updateUserInfo(data: any): Promise<void> {
    try {
      // WL locationId in case of Gokollab
      const locationId = useGlobalStore().$state.locationId
      const id = useGlobalStore().$state.userId

      const response = await restAgent.ClientPortal.updateUserDetails(
        locationId,
        id,
        data
      )
      const gokollabContactId = await this.getGokollabContactId(locationId, id)
      this.state.currentUser = { ...response.data, gokollabContactId }
      Theme.state.isDarkMode = this.state.currentUser?.darkMode ?? false

      // useGlobalStore().setProfileDetails(this.state.currentUser)
    } catch (error) {
      console.error(error)
    }
  }

  checkMandatedInfoPresentOrNot(): boolean {
    if (
      app.state.fetching ||
      !this.state.currentUser ||
      GroupState.state.groupData?.settings?.showMandatoryInfoModal == false
    ) {
      return true
    }
    return this.state.currentUser?.avatar !== undefined
  }

  setMandatoryInfoModal(value: boolean): void {
    this.state.showMandatoryInfoModal = value
  }

  async getUserDetails(userSlug: string): Promise<any> {
    return await GlobalProfileService.getGlobalUserProfile(userSlug)
  }
}

export const UserState = new UsersState()
